import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { AdvertSnippetBlock as Value } from "@reactivated";

import { getLinkUrl } from "../../utils/wagtail";

type IProps = Immutable<{
    value: Value;
}>;

type IAdvertChild = NonNullable<Immutable<Value>["advert"]>["body"][number];

const renderAdvertChild = (block: IAdvertChild): React.JSX.Element => {
    switch (block.type) {
        case "title":
            return (
                <h3 className="banner__title" key={block.id}>
                    {block.value}
                </h3>
            );
        case "description":
            return (
                <p className="banner__copy" key={block.id}>
                    {block.value}
                </p>
            );
        case "cta":
            // rel="noreferrer" is a deliberate (visually inert) addition
            // over the legacy markup: eslint react/jsx-no-target-blank
            // requires it on target="_blank" links.
            return (
                <a
                    key={block.id}
                    className="banner__cta button"
                    href={getLinkUrl(block.value)}
                    target="_blank"
                    rel="noreferrer"
                    title={block.value.html_title ?? ""}
                >
                    {block.value.display_name}
                </a>
            );
    }
};

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/adverts/advert_snippet.html`` (+ its title/description/cta
 * child templates) (#33285). ``advert`` is null only when the (required)
 * SnippetChooserBlock resolves no snippet; the legacy template then emitted
 * an empty ``section.banner`` shell, which this skips entirely (nothing
 * visible either way).
 */
export const AdvertSnippetBlock = (props: IProps): React.JSX.Element | null => {
    const advert = props.value.advert;
    if (!advert) {
        return null;
    }
    return (
        <section className="banner banner--reverse ptb" id="cocoon">
            <figure className="banner__figure">
                <img
                    className="img-responsive"
                    src={advert.image_url ?? ""}
                    alt={t`Cocoon by Sealy`}
                    loading="lazy"
                    decoding="async"
                />
            </figure>
            <div className="banner__content">
                {advert.body.map(renderAdvertChild)}
            </div>
        </section>
    );
};
